import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './MediaCenter.module.scss';
import useIsRtl from '../../../../app/helpers/isRtl';
import defaultImage from '../../../../assets/images/defaultNews.png';
import { useGetQuery } from '../../../../services/apiCall';
import endpoints from '../../../../api/endpoints';
import NewsCard from '../../../atoms/newsCard/NewsCard';
import useScrollToTop from '../../../../app/hooks/useScrollToTop';
import Text from '../../../atoms/text/Text';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../atoms/loader/Loader';
import NoData from '../../../organism/filterContainer/factory/noData/noData';
import { SortMenu } from '../newsTemplate/NewsPage';
import NewsSearchModal from '../newsTemplate/news-search-modal/NewsSearchModal';

const LIST_ENDPOINTS = {
  news: endpoints.news.getNews,
  events: endpoints.news.getEvents,
  media: endpoints.news.mediaGallery,
  newsletter: endpoints.news.getNewsletter,
};

const pickList = (payload) =>
  Array.isArray(payload) ? payload : (payload?.items ?? payload?.data ?? []);

const normalizeItem = (type, item) => {
  switch (type) {
    case 'news':
      return {
        id: item?.id ?? item?.newsId ?? item?.Id,
        title: item?.title_AR ?? '',
        date: item?.newsDate ?? null,
        image: item?.image ? `${item?.url ?? ''}${item?.image}` : defaultImage,
        type,
      };
    case 'events':
      return {
        id: item?.id ?? item?.eventId ?? item?.Id,
        title: item?.title_AR ?? '',
        date: item?.eventDate ?? null,
        time: item?.eventDate ?? null,
        image: item?.image1
          ? `${item?.url ?? ''}${item?.image1}`
          : item?.image
            ? `${item?.url ?? ''}${item?.image}`
            : defaultImage,
        type,
      };
    case 'media':
      return {
        id: item?.mediaGalleryId ?? item?.id ?? item?.galleryId ?? item?.Id,
        title: item?.galleryName_AR ?? '',
        date: item?.images?.[0]?.createdOn ?? null,
        image: item?.images?.[0]
          ? `${item.images[0].url ?? ''}${item.images[0].imageURL ?? ''}`
          : defaultImage,
        gallery: {
          images: Array.isArray(item?.images)
            ? item.images.map((img) => `${img.url ?? ''}${img.imageURL ?? ''}`)
            : [],
        },
        type,
      };
    case 'newsletter':
      return {
        id: item?.id ?? item?.newsletterId ?? item?.eventId ?? item?.Id,
        title: item?.title_AR ?? '',
        date: item?.newsletterDate ?? null,
        image: item?.newsletterImage
          ? `${item?.url ?? ''}${item?.newsletterImage}`
          : defaultImage,
        description: item?.body_AR ?? '',
        type,
      };
    default:
      return { id: null, title: '', date: null, image: defaultImage, type };
  }
};

const Section = React.memo(function Section({
  className = '',
  imageWrapper,
  title,
  items = [],
  isLoading = false,
  isRtl = false,
  to,
  isCLickable,
  sortBy = 'date',
  containerRef,
}) {
  const navigate = useNavigate();

  const handleViewMore = React.useCallback(() => {
    navigate(`/news-and-events/${to}`);
  }, [navigate, to]);

  const itemsSorted = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    return [...list].sort((a, b) => {
      if (sortBy === 'title') {
        const av = (a?.title ?? '').toString();
        const bv = (b?.title ?? '').toString();
        return av.localeCompare(bv, undefined, { sensitivity: 'base' });
      }
      const ad = a?.date ? new Date(a.date).getTime() : NaN;
      const bd = b?.date ? new Date(b.date).getTime() : NaN;
      if (Number.isNaN(ad) && Number.isNaN(bd)) return 0;
      if (Number.isNaN(ad)) return 1;
      if (Number.isNaN(bd)) return -1;
      return bd - ad;
    });
  }, [items, sortBy]);

  return (
    <section className={styles.section}>
      <div className={`${styles.sectionHeader} ${isRtl ? styles.rtl : ''}`}>
        <Text
          title={title}
          variant="text-bold-20"
          color="black"
          className={styles.text}
        />
        <button type="button" onClick={handleViewMore}>
          <Text
            title="common.view_all"
            variant="text-bold-italic-15"
            color="black"
            className={styles.text}
          />
        </button>
      </div>

      {isLoading ? (
        <div style={{ width: '100%', height: '50vh' }}>
          <Loader />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <NoData />
        </div>
      ) : (
        <div
          className={`${styles.grid} ${isRtl ? styles.rtl : ''}`}
          ref={containerRef}
        >
          {itemsSorted.slice(0, 4).map((item) => (
            <div
              key={`${item?.type ?? 'item'}-${item?.id ?? item?.newsId ?? item?.Id}`}
              className={styles.searchable}
              data-search-item
              data-date={item.date || ''}
              data-type={item.type}
            >
              <NewsCard
                className={className}
                imageWrapper={imageWrapper}
                isCLickable={isCLickable}
                type={to}
                {...item}
                isGrid
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
});
const MediaCenter = () => {
  useScrollToTop();
  const isRtl = useIsRtl();
  const [sortBy, setSortBy] = useState('date');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const newsRef = useRef(null);
  const eventsRef = useRef(null);
  const mediaRef = useRef(null);
  const newsletterRef = useRef(null);
  const PAGE_SIZE = 4;
  const PAGE_NUMBER = 1;
  const searchRefs = useMemo(
    () => [newsRef, eventsRef, mediaRef, newsletterRef],
    []
  );

  const { data: newsData, isLoading: isNewsLoading } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.news,
    params: [PAGE_SIZE.toString(), PAGE_NUMBER.toString(), '?IsActive=true'],
  });

  const { data: eventsData, isLoading: isEventsLoading } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.events,
    params: [PAGE_SIZE.toString(), PAGE_NUMBER.toString(), '?IsActive=true'],
  });

  const { data: mediaData, isLoading: isMediaLoading } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.media,
  });

  const { data: newsletterData, isLoading: isNewsletterLoading } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.newsletter,
    params: [PAGE_SIZE.toString(), PAGE_NUMBER.toString(), '?IsActive=true'],
  });

  const newsItems = useMemo(
    () =>
      pickList(newsData)
        .map((n) => normalizeItem('news', n))
        .filter((x) => x.id),
    [newsData]
  );

  const eventItems = useMemo(
    () =>
      pickList(eventsData)
        .map((n) => normalizeItem('events', n))
        .filter((x) => x.id),
    [eventsData]
  );

  const mediaItems = useMemo(
    () =>
      (Array.isArray(mediaData) ? mediaData : (mediaData?.items ?? []))
        .slice(0, 4)
        .map((n) => normalizeItem('media', n))
        .filter((x) => x.id),
    [mediaData]
  );

  const newsletterItems = useMemo(
    () =>
      pickList(newsletterData)
        .map((n) => normalizeItem('newsletter', n))
        .filter((x) => x.id),
    [newsletterData]
  );
  const onToggleSort = useCallback((next) => {
    setIsSortOpen(Boolean(next));
  }, []);
  const openSearchStable = useCallback(() => setIsSearchOpen(true), []);
  const closeSearch = useCallback(() => setIsSearchOpen(false), []);
  return (
    <div className={`${styles.mediaCenter}`}>
      <div className={styles['header-title']}>
        <Text
          title="common.News_And_Events"
          variant="text-bold-italic-30"
          color="black"
        />
      </div>
      <div>
        <SortMenu
          isRtl={isRtl}
          isOpen={isSortOpen}
          onToggle={onToggleSort}
          sortBy={sortBy}
          onPick={setSortBy}
          setIsSearchOpen={openSearchStable}
        />
        <div className={styles['sections']}>
          <Section
            title="common.news"
            items={newsItems}
            isLoading={isNewsLoading}
            imageWrapper={styles['image-news']}
            isRtl={isRtl}
            to="news"
            sortBy={sortBy}
            containerRef={newsRef}
          />
          <Section
            title="common.events"
            items={eventItems}
            isLoading={isEventsLoading}
            imageWrapper={styles['image-news']}
            isRtl={isRtl}
            to="events"
            sortBy={sortBy}
            containerRef={eventsRef}
          />
          <Section
            title="MediaGallery.MEDIA GALLERY"
            items={mediaItems}
            isLoading={isMediaLoading}
            imageWrapper={styles['image-news']}
            isRtl={isRtl}
            to="media"
            isCLickable={true}
            sortBy={sortBy}
            containerRef={mediaRef}
          />
          <Section
            title="common.newsletter"
            items={newsletterItems}
            isLoading={isNewsletterLoading}
            className={styles['newsletter-card']}
            imageWrapper={styles['image-wrapper']}
            isRtl={isRtl}
            to="news-letter"
            sortBy={sortBy}
            containerRef={newsletterRef}
          />
        </div>
        <NewsSearchModal
          isOpen={isSearchOpen}
          onClose={closeSearch}
          containerRefs={searchRefs}
        />
      </div>
    </div>
  );
};

export default MediaCenter;
